// src/components/fnct/ChoixMachineFnct.tsx — SCE Issues
// « Une FNC-T, sur quel équipement ? » (15/10, Guillaume : « machine d'abord ») :
// depuis le bouton central (« + › Une FNC-T ») et « + Nouvelle FNC-T » de /fnct.
// Même panneau que « Une NC-S, sur quel problème ? » (styles `.declarer-*`) :
// machines en fabrication d'office, recherche dans le référentiel (N° de série,
// OF, PN, CVE). Le choix ouvre la fiche équipement sur la FNC-T (`ouvrirFnct`) :
// le même parcours que son bouton « Ouvrir une FNC-T ».

import { useEffect, useMemo, useState, type JSX } from 'react'
import { ArrowLeft, Search, X } from 'lucide-react'
import { listAllEquipements } from '@/api/equipements.api'
import { useEnFabrication } from '@/hooks/useEnFabrication'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { machinesPourFnct } from '@/utils/choixMachineFnct'
import type { Equipement } from '@/types/issue.types'

export function ChoixMachineFnct({
  onChoisir,
  onRetour,
  onClose,
}: {
  onChoisir: (eq: Equipement) => void
  /** Retour au choix « Que voulez-vous déclarer ? » (bouton central) ; absent depuis /fnct. */
  onRetour?: () => void
  onClose: () => void
}): JSX.Element {
  const estBureau = useEstBureau()
  const { cles } = useEnFabrication()
  const [referentiel, setReferentiel] = useState<Equipement[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [recherche, setRecherche] = useState('')

  useEffect(() => {
    let annule = false
    listAllEquipements()
      .then((r) => {
        if (!annule) setReferentiel(r)
      })
      .catch((err: unknown) => {
        if (!annule) setErreur(err instanceof Error ? err.message : 'Référentiel des équipements illisible')
      })
    return () => {
      annule = true
    }
  }, [])

  const machines = useMemo(() => machinesPourFnct(referentiel ?? [], recherche, cles), [referentiel, recherche, cles])
  const enRecherche = recherche.trim().length >= 2

  return (
    <div className="declarer-panneau" role="dialog" aria-modal="true" aria-label="Une FNC-T, sur quel équipement ?">
      <div className="declarer-panneau-entete">
        {onRetour && (
          <button type="button" className="declarer-icone-bouton" onClick={onRetour} aria-label="Retour au choix">
            <ArrowLeft size={22} />
          </button>
        )}
        <h2 className="declarer-panneau-titre">Une FNC-T, sur quel équipement ?</h2>
        <button type="button" className="declarer-icone-bouton" onClick={onClose} aria-label="Fermer">
          <X size={22} />
        </button>
      </div>

      <div className="declarer-panneau-corps">
        <p className="declarer-intro">
          Une FNC-T porte sur un équipement. Un problème pourra y être lié, s’il y en a un.
        </p>

        <label className="declarer-recherche">
          <Search size={18} color="var(--color-gray-60)" aria-hidden="true" />
          <input
            type="search"
            autoFocus={estBureau}
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="N° de série, OF, PN, CVE"
            aria-label="Chercher un équipement"
          />
        </label>

        {erreur && <p className="declarer-vide" style={{ color: 'var(--color-traffic-red)' }}>{erreur}</p>}
        {!erreur && referentiel === null && <p className="declarer-vide">Chargement du référentiel…</p>}
        {referentiel !== null && machines.length === 0 && (
          <p className="declarer-vide">
            {enRecherche ? `Aucun équipement ne correspond à « ${recherche.trim()} ».` : 'Tapez un N° de série, un OF ou un PN.'}
          </p>
        )}

        {machines.length > 0 && (
          <section>
            <h3 className="declarer-section-titre">{enRecherche ? 'Résultats' : 'En fabrication'}</h3>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {machines.map((eq, i) => (
                <li key={eq.numeroSerie}>
                  <button type="button" className="declarer-ligne" style={{ animationDelay: `${Math.min(i, 12) * 0.025}s` }} onClick={() => onChoisir(eq)}>
                    <span className="declarer-machine">{eq.numeroSerie}</span>
                    <span className="declarer-ligne-texte">
                      <span className="declarer-ligne-titre">{eq.familleEquipement || 'Modèle non renseigné'}</span>
                      <span className="declarer-ligne-meta">{[eq.pn, eq.of, eq.cve].filter(Boolean).join(' · ')}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  )
}
